<template>
  <div class="flex flex-1 w-full flex-row-reverse md:w-auto md:flex-row items-center space-x-2 bg-focus h-14 lg:h-16 px-3 lg:px-6 shadow print:hidden">
    <button
      id="embed-back-btn"
      class="cursor-pointer mr-1 md:mr-4"
      :class="{ hidden: !showEmbedBackButton }"
      type="button"
      @click.prevent="goBack"
    >
      &laquo;
      {{ t('common.back') }}
    </button>

    <Menu
      id="hamburger-menu"
      class="pr-2 md:right-0"
      :class="{ hidden: hideHamburgerButton }"
      :dropdown-classes="['right-0', 'md:left-0']"
      :button-classes="['flex', 'flex-column', 'items-center', 'border-0', 'shadow-none']"
      :button-aria-label="t('layouts.navigation.accordion_menu')"
    >
      <template #button>
        <LzIcon path="icons/hamburger" class="h-8 w-8 text-primary" aria-hidden="true" />
      </template>

      <template #listItems>
        <li class="text-important hover:bg-focus bold px-1 py-2 md:hidden">{{ t('layouts.header.explore') }}</li>
        <li class="md:hidden md:rounded-t-lg">
          <SearchForm :id="'search-input'" :placeholder="t('common.search_placeholder_short')" />
        </li>

        <MenuItem class="md:rounded-t-lg">
          <a :href="Routes.root_path()" :class="isHomeActive ? 'text-accent underline' : ''">{{ t('layouts.header.home_link') }}</a>
        </MenuItem>

        <MenuItem
          v-for="(navOption, index) in customNavOptions"
          :key="`nav-option-${index}`"
        >
          <a :href="navOption.url" :class="isActiveLink(navOption.url) ? 'text-accent underline' : ''">{{ navOption.label }}</a>
        </MenuItem>

        <li class="text-important hover:bg-focus bold px-1 py-2 md:hidden">{{ t('layouts.user_bar.my_account') }}</li>

        <template v-if="isLoggedIn">
          <MenuItem v-for="(accountLink, index) in navLinks" :key="`mobile-account-link-${index}`" class="md:hidden">
            <a
              :href="accountLink.url"
              :data-track-click="accountLink.trackClick || null"
              class="inline-flex items-center"
              :class="isActiveLink(accountLink.url) ? 'text-accent underline' : ''"
            >
              {{ accountLink.label }}
              <InlineSvg v-if="accountLink.icon" :path="accountLink.icon" class="w-3 h-3 ml-0.5 fill-current" aria-hidden="true" />
            </a>
          </MenuItem>
          <UserActionsMobile class="flex items-center md:hidden" />
        </template>

        <MenuItem v-else class="md:hidden">
          <a :href="Routes.login_path()">{{ t('common.login') }}</a>
        </MenuItem>
      </template>
    </Menu>

    <input id="original-request-url" type="hidden" :value="originalRequestUrl" />
    <!-- Logo -->
    <div class="md:flex-none flex-1 flex flex-column items-center">
      <a id="home-icons" :href="homeIconHref" :target="homeIconTarget" :rel="homeIconRel" class="inline-flex items-center">
        <InlineSvg path="logo/logo-emblem" class="h-8 lg:h-10 md:hidden" />
        <InlineSvg path="logo/logo" class="md:h-7 lg:h-9 hidden md:inline-block" />
        <span class="sr-only">{{ t('layouts.header.logo_link') }}</span>
      </a>
    </div>

    <div id="search-nav" class="flex flex-1 items-center hidden justify-end" :class="searchNavVisibilityClass">
      <SearchForm :id="'mobile-search-form-feature'" ref="searchFormRef" class="w-full max-w-sm inline-block" />
      <button
        id="close-search"
        :aria-label="t('common.close')"
        class="h-10 w-10 flex items-center text-important hover:text-important-hover cursor-pointer"
        type="button"
        @click="closeSearch"
      >
        <LzIcon path="icons/close" class="margin-block h-6 w-6" aria-hidden="true" />
      </button>
    </div>

    <div
      id="right-nav"
      role="navigation"
      :aria-label="t('layouts.navigation.primary')"
      class="flex flex-1 h-full hidden items-center justify-end space-x-5"
      :class="rightNavVisibilityClass"
    >
      <a
        class="flex items-center h-full font-semibold hover:text-important-hover relative"
        :class="isHomeActive ? 'nav-active text-accent' : ' text-important'"
        :href="Routes.root_path()"
        :aria-current="isHomeActive ? 'page' : null"
      >
        {{ t('layouts.header.home_link') }}
      </a>

      <template v-if="isLoggedIn">
        <a
          v-for="(accountLink, index) in navLinks"
          :key="`desktop-account-link-${index}`"
          class="flex items-center h-full font-semibold hover:text-important-hover relative"
          :class="isActiveLink(accountLink.url) ? 'nav-active text-accent' : ' text-important'"
          :href="accountLink.url"
          :data-track-click="accountLink.trackClick || null"
          :aria-current="isActiveLink(accountLink.url) ? 'page' : null"
        >
          {{ accountLink.label }}
          <InlineSvg v-if="accountLink.icon" :path="accountLink.icon" class="w-3 h-3 ml-0.5 fill-current" aria-hidden="true" />
        </a>

        <button
          id="open-search"
          :aria-label="t('common.search_placeholder_short')"
          class="inline-block h-10 w-10 p-2 cursor-pointer relative"
          :class="currentPath.startsWith('/search') ? 'nav-active' : ''"
          type="button"
          @click="openSearch"
        >
          <LzIcon path="icons/search" :class="currentPath.startsWith('/search') ? 'text-accent' : 'text-important group-hover:text-important-hover'" class="h-6 w-6" aria-hidden="true" />
        </button>

        <UserActions class="flex items-center" />
      </template>

      <template v-else>
        <button
          id="open-search"
          :aria-label="t('common.search_placeholder_short')"
          class="inline-block h-10 w-10 p-2 cursor-pointer"
          type="button"
          @click="openSearch"
        >
          <LzIcon path="icons/search" class="text-important group-hover:text-important-hover h-6 w-6" aria-hidden="true" />
        </button>

        <Menu
          v-if="showStudentLoginDropdown"
          id="user-login"
          :dropdown-classes="['right-0']"
          :button-classes="['btn--primary', 'btn--small']"
        >
          <template #button>
            {{ t('common.login') }}
          </template>

          <template #listItems>
            <MenuItem>
              <a :href="Routes.login_path()">{{ t('common.teacher') }}</a>
            </MenuItem>
            <MenuItem>
              <a :href="Routes.student_enhanced_login_path()">{{ t('common.student') }}</a>
            </MenuItem>
          </template>
        </Menu>

        <a v-else :href="loginButtonPath" class="btn btn--primary btn--small">{{ t('common.login') }}</a>
      </template>
    </div>
  </div>
  <ThemeModal />
  <AvatarModal />
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { location as $location } from 'utils'
import Routes from 'routes'
import SearchForm from 'vue_features/search/components/SearchForm'
import UserActions from 'vue_features/shared/components/UserActions'
import UserActionsMobile from 'vue_features/shared/components/UserActionsMobile'
import ThemeModal from 'vue_features/themes/components/modals/ThemeModal'
import AvatarModal from 'vue_features/avatars/components/modals/AvatarModal'
import { InlineSvg, LzIcon } from 'vue_features/shared/components/ui'
import Menu from 'vue_features/shared/components/ui/Menu'
import MenuItem from 'vue_features/shared/components/ui/MenuItem.vue'
import { useCurrentUserStore, useCurrentCustomerStore } from 'vue_features/shared/store/composables'

const { t } = useI18n()
const { isLoggedIn, authorizedAsTeacher } = useCurrentUserStore()
const { state: customerState } = useCurrentCustomerStore()

const props = defineProps({
  hideMenu: { type: Boolean, default: false },
  customNavOptions: { type: Array, default: () => [] },
  rewardsPath: { type: String, default: null },
  collectionsPath: { type: String, default: null },
  dataDashboardPath: { type: String, default: null },
  pilotGuidePath: { type: String, default: null },
  isPremium: { type: Boolean, default: false },
})

const searchFormRef = ref(null)
const showSearch = ref(false)
const showEmbedBackButton = ref(false)
const hideHamburgerButton = ref(false)
const homeIconHref = ref(window.location.origin)
const homeIconTarget = ref(null)
const homeIconRel = ref(null)
const originalRequestUrl = ref(window.location.href)
const showStudentLoginDropdown = computed(() => !isLoggedIn.value && customerState.hasStudentFunctionality)

const loginButtonPath = computed(() => props.isPremium ? Routes.login_path() : Routes.customer_selects_path())

const classesPath = computed(() => {
  if (!isLoggedIn.value || !customerState.hasStudentFunctionality) return null
  return authorizedAsTeacher.value ? Routes.teacher_klasses_path() : Routes.student_klasses_path()
})

const navLinks = computed(() => {
  const links = []

  if (props.pilotGuidePath) {
    links.push({ label: t('layouts.user_bar.pilot_guide'), url: props.pilotGuidePath, icon: 'icons/bolt-filled' })
  }
  if (customerState.hasStudentFunctionality && classesPath.value) {
    links.push({ label: t('layouts.user_bar.classes'), url: classesPath.value })
  }
  if (props.rewardsPath) {
    links.push({ label: t('layouts.user_bar.rewards'), url: props.rewardsPath })
  }
  if (props.collectionsPath) {
    links.push({ label: t('layouts.user_bar.collections'), url: props.collectionsPath })
  }
  if (props.dataDashboardPath) {
    links.push({ label: t('layouts.user_bar.data'), url: props.dataDashboardPath, trackClick: 'Data Dashboard nav link clicked' })
  }

  links.push({ label: t('layouts.user_bar.saved_lessons'), url: Routes.bookmarks_url() })
  links.push({ label: t('layouts.user_bar.help'), url: Routes.page_path('help') })
  return links
})

const currentPath = window.location.pathname

const isHomeActive = computed(() => {
  const homePath = customerState.homePath
  if (!homePath || homePath === '/') return currentPath === '/'
  const homeBase = homePath.split('?')[0]
  if (homeBase === '/') return currentPath === '/'
  return currentPath.startsWith(homeBase)
})

function isActiveLink(url) {
  if (!url) return false
  const linkPath = new URL(url, window.location.origin).pathname
  if (linkPath.startsWith('/help')) return customerState.helpPath && currentPath.startsWith(customerState.helpPath)
  if (linkPath.endsWith('/classes')) return currentPath === linkPath
  if (linkPath.startsWith('/data_dashboard')) return currentPath.startsWith(linkPath) || currentPath.startsWith('/item_analysis')
  return currentPath.startsWith(linkPath)
}

const rightNavVisibilityClass = computed(() => (showSearch.value ? 'md:hidden' : 'md:flex'))
const searchNavVisibilityClass = computed(() => (showSearch.value ? 'md:flex' : 'md:hidden'))

const openSearch = () => {
  showSearch.value = true
  nextTick(() => searchFormRef.value?.focus?.())
}

const closeSearch = () => {
  showSearch.value = false
}

const goBack = () => {
  window.history.back()
}

onMounted(() => {
  showSearch.value = $location.pathname().split('/')[1] === 'search'

  const isEmbedded = window.top !== window.self
  if (isEmbedded) {
    hideHamburgerButton.value = true
    if (window.history.length > 1 && document.referrer.includes($location.current().host)) {
      showEmbedBackButton.value = true
    }

    homeIconHref.value = originalRequestUrl.value
    homeIconTarget.value = '_blank'
    homeIconRel.value = 'noopener noreferrer'

    document.getElementById('footer')?.classList.add('hidden')
    document.getElementById('embed-footer')?.classList.remove('hidden')
    document.querySelector('#header > div')?.classList.remove('lg:h-16')
    document.querySelectorAll('#header .dropdown-pane').forEach((dropdown) => dropdown.classList.remove('lg:mt-4'))
  }

})
</script>

<style scoped>
.nav-active::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 rounded-sm bg-accent;
}
</style>
